import { BlogCategory, BlogLanding, BlogPost, GlossaryTerm } from 'types/prismic/blog';
import {
	EreitsLandingPage,
	FormattedHDYHMultipleChoice,
	HDYHChoice,
	HdyhFollowUp,
	HdyhMultipleChoiceSources,
	HDYHMultipleSlide,
	HdyhSources,
	MediaPartners,
	OfferingsLandingPage,
	PortfolioStats,
	PrismicDocument,
	PrismicRef,
	RealEstateStrategies,
	ReviewsData,
	ReviewsPanelData,
	SocialStats
} from 'types/prismic';
import { apiRequest } from '@src/utils/request';
import axios from 'axios';
import { LandingPageData } from 'types/prismic/landing-page';
import { NewsletterLandingPageData } from 'types/prismic/newsletter';

async function getBaseUrl(customRef?: string): Promise<string> {
	const prismicHost = 'https://fundrise.cdn.prismic.io/api/v2';
	const prismicBaseUrl = `${prismicHost}/documents/search?ref=`;
	if (customRef) {
		return prismicBaseUrl + customRef;
	} else {
		const response = await axios.get(prismicHost);
		const refs: Array<PrismicRef> = response.data.refs;
		return prismicBaseUrl + refs[0].ref;
	}
}

function getBlogPostUrl(baseUrl: string, urlSlug: string): string {
	return `${baseUrl}&q=[[at(my.blog.uid, "${urlSlug}")]]&fetchLinks=category.name,author.name,blog.title,blog.abstract,blog.display_date`;
}

function getBlogsByCategoriesUrl(baseUrl: string, categoryId: string, limit: number): string {
	return `${baseUrl}&q=[[at(document.type, "blog")][at(my.blog.category, "${categoryId}")]]&orderings=[my.blog.display_date desc]&pageSize=${limit}&fetchLinks=category.name,category.description,author.name,blog.title,blog.abstract,blog.display_date`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getBlogPostFromResponse(response: any): BlogPost | null {
	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : null;
}

export async function getBlogPost(urlSlug: string, customRef?: string): Promise<BlogPost | null> {
	const baseUrl = await getBaseUrl(customRef);

	const response = await axios.get(getBlogPostUrl(baseUrl, urlSlug));

	return getBlogPostFromResponse(response);
}

export async function getBlogPosts(urlSlugs: Array<string>, customRef?: string): Promise<Array<BlogPost>> {
	const baseUrl = await getBaseUrl(customRef);
	const requestUrls = urlSlugs.map((slug) => getBlogPostUrl(baseUrl, slug));

	const responses = await axios.all(requestUrls.map((url) => axios.get(url)));

	const result: Array<BlogPost> = [];

	responses.forEach((response) => {
		const blog = getBlogPostFromResponse(response);
		if (blog !== null) {
			result.push(blog);
		}
	});
	return result;
}

export async function getBlogLanding(customRef?: string): Promise<BlogLanding | null> {
	const baseUrl = await getBaseUrl(customRef);
	const response = await axios.get(
		`${baseUrl}&q=[[at(document.type, "blog_landing")]]&fetchLinks=categories.category_name,blog.title,blog.abstract,blog.cover_image`
	);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : null;
}

export async function getBlogCategories(customRef?: string): Promise<Array<BlogCategory>> {
	const baseUrl = await getBaseUrl(customRef);
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "category")]]`);

	return response.data.results;
}

export async function getBlogCategory(categorySlug: string, customRef?: string): Promise<BlogCategory | null> {
	const baseUrl = await getBaseUrl(customRef);
	const requestUrl = `${baseUrl}&q=[[at(my.category.uid, "${categorySlug}")]]`;
	const response = await axios.get(requestUrl);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : null;
}

export async function getBlogsByCategory(categoryId: string, limit = 5, customRef?: string): Promise<Array<BlogPost>> {
	const baseUrl = await getBaseUrl(customRef);
	const requestUrl = getBlogsByCategoriesUrl(baseUrl, categoryId, limit);

	const response = await axios.get(requestUrl);

	return response.data.results || [];
}

export async function getBlogsByCategories(
	categoryIds: Array<string>,
	limit = 5,
	customRef?: string
): Promise<Array<Array<BlogPost>>> {
	const baseUrl = await getBaseUrl(customRef);
	const requestUrls = categoryIds.map((categoryId) => getBlogsByCategoriesUrl(baseUrl, categoryId, limit));

	const responses = await axios.all(requestUrls.map((url) => axios.get(url)));

	return responses.map((response) => response.data.results || []);
}

export async function getPreview(previewToken: string, documentId: string): Promise<PrismicDocument> {
	const baseUrl = await getBaseUrl(previewToken);
	const requestUrl = `${baseUrl}&q=[[at(document.id, "${documentId}")]]`;
	const response = await axios.get(requestUrl);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : {};
}

export async function getSocialStats(): Promise<SocialStats> {
	const baseUrl = await getBaseUrl();
	const requestUrl = `${baseUrl}&q=[[at(document.type, "social_stats")]]`;
	const response = await axios.get(requestUrl);

	return response.data.results[0].data;
}

export async function getReviewsData(): Promise<ReviewsData> {
	const baseUrl = await getBaseUrl();
	const requestUrl = `${baseUrl}&q=[[at(document.type, "reviews")]]`;
	const response = await axios.get(requestUrl);

	return response.data.results[0].data;
}

export async function getGlossaryTerm(termSlug: string, customRef?: string): Promise<GlossaryTerm | null> {
	const baseUrl = await getBaseUrl(customRef);
	const requestUrl = `${baseUrl}&q=[[at(my.glossary.uid, "${termSlug}")]]`;
	const response = await axios.get(requestUrl);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : null;
}

export async function getOrderedGlossaryTerms(limit: number, customRef?: string): Promise<Array<GlossaryTerm>> {
	const baseUrl = await getBaseUrl(customRef);
	const response = await axios.get(
		`${baseUrl}&q=[[at(document.type, "glossary")]]&orderings=[my.glossary.title]&pageSize=${limit}`
	);

	return response.data.results;
}

export async function getMediaPartners(): Promise<MediaPartners | null> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "media_partners")]]`);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}

export async function getHdyhMultipleChoiceSources(): Promise<FormattedHDYHMultipleChoice | null> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "hdyh_sources")]]`);
	const otherSource = { source: 'Other' };

	if (response.data.results && response.data.results.length > 0) {
		const survey = response.data.results[0].data;
		const choices: Array<HDYHChoice> = [];

		survey?.body?.forEach((option: HdyhMultipleChoiceSources) => {
			let followUpSlide: HDYHMultipleSlide | undefined = undefined;
			const followUpChoices: Array<HDYHChoice> = [];

			if (option.items && option.items?.length > 0) {
				option.items.forEach((followUp: HdyhFollowUp) => {
					if (followUp.follow_up_option) {
						followUpChoices.push({
							source: followUp.follow_up_option
						});
					}
				});

				if (followUpChoices.length > 0) {
					followUpChoices.push(otherSource);

					followUpSlide = {
						question: option.primary.follow_up_question || survey.survey_question,
						choices: followUpChoices
					};
				}
			}

			if (option.primary.response) {
				choices.push({
					source: option.primary.response,
					followUp: followUpSlide
				});
			}
		});

		choices.push(otherSource);

		const slide: HDYHMultipleSlide = {
			question: survey.survey_question,
			choices: choices
		};

		return {
			headline: survey.survey_headline,
			slide: slide
		};
	}

	return null;
}

export async function getHdyhSources(): Promise<HdyhSources | null> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "hdyh_sources")]]`);
	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}

export async function getRealEstateStrategies(): Promise<Array<RealEstateStrategies>> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "re_strategies")]]`);

	return response.data.results;
}

export async function getPortfolioStats(): Promise<PortfolioStats> {
	const baseUrl = await getBaseUrl();
	const prismicRequestUrl = axios.get(`${baseUrl}&q=[[at(document.type, "social_stats")]]`);
	const reitElementStrategyTotalsUrl = apiRequest.get(`/reit-elements/strategy-totals`);

	const responses = await axios.all([prismicRequestUrl, reitElementStrategyTotalsUrl]);

	return {
		prismicStats: responses[0].data.results[0].data,
		reitsTotals: responses[1].data
	};
}

export async function getAdvisorsTrackRecord(): Promise<PrismicDocument> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(
		`${baseUrl}&q=[[at(document.type, "advisors_track_record")]]&fetchLinks=blog.title,blog.abstract,blog.cover_image,blog.display_date`
	);
	return response.data.results && response.data.results.length > 0 ? response.data.results[0] : null;
}

export async function getEreitsLanding(): Promise<EreitsLandingPage> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "ereits_landing")]]`);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}

export async function getReviewsPanelData(): Promise<ReviewsPanelData> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(my.global_content.uid, "reviews-panel")]]`);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data.body[0] : null;
}

export async function getLandingPage(pageSlug: string): Promise<LandingPageData> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(
		`${baseUrl}&q=[[at(my.landing_page.uid, "${pageSlug}")]]&fetchLinks=blog.title,blog.cover_image,global_content.body`
	);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}

export async function getNewsletter(): Promise<NewsletterLandingPageData> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "newsletter")]]`);

	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}

export async function getOfferingsLanding(): Promise<OfferingsLandingPage> {
	const baseUrl = await getBaseUrl();
	const response = await axios.get(`${baseUrl}&q=[[at(document.type, "offerings_landing")]]`);
	return response.data.results && response.data.results.length > 0 ? response.data.results[0].data : null;
}
